//* Necessary for typing in actions and getters.
/* eslint-disable @typescript-eslint/no-use-before-define, no-use-before-define */
//* We would like the errors to appear in the console if they happen.
/* eslint-disable no-console */

import { ActionContext } from 'vuex';
import * as App from '@/types/app';
import * as Database from '@/types/database';
import { useFirebaseAuth, useFirestore } from '@/services';
import { mapDatabaseBaseModelToAppBaseModel, mapAppBaseModelToDatabaseBaseModel, unsafeTypeCast } from '@/utils';
import { moduleActionContext, moduleGetterContext } from '@/store';
import { defineCustomFirebaseCollectionStoreModule } from './base-module';

const { useUserOperators } = useFirestore();

export const usersModule = defineCustomFirebaseCollectionStoreModule<App.User, Database.User, Record<string, unknown>>(
  useUserOperators,
  {
    getters: {
      getCurrentUser: (...context): App.User | undefined => {
        const { state } = getterContext(context);
        const { auth } = useFirebaseAuth();

        const user = state.data.firestore.find(databaseUser => databaseUser.id === auth.currentUser?.uid);
        const mappedUser = user && mapDatabaseBaseModelToAppBaseModel(user);
        return mappedUser as App.User | undefined;
      }
    },
    actions: {
      create: async (context, user: App.User): Promise<void> => {
        const { state, dispatch, rootDispatch } = actionContext(context);

        const groupId = await rootDispatch.groups.getGroupId();
        if (!groupId) return;

        const userOperators = useUserOperators(groupId);
        const expectedPayload = [...state.data.firestore, unsafeTypeCast<Database.User>(user)];
        const ensureExpectedPayload = await dispatch.ensureFirestoreListener(expectedPayload);

        userOperators
          .create(unsafeTypeCast<Database.User>(user))
          .then(() => ensureExpectedPayload())
          .catch(error => console.error('Error creating user:', user, error));
      },
      update: async (context, user: App.User): Promise<void> => {
        const { state, getters, dispatch, rootDispatch } = actionContext(context);

        if (!user.id) return;
        if (!getters.getCurrentUser?.authorityLevel) return;
        if (!user.authorityLevel) return;
        if (getters.getCurrentUser.authorityLevel < user.authorityLevel) return;

        if (getters.getCurrentUser?.id == user.id) {
          console.error('Error updating user: Current user cannot update their own settings');
          return;
        }

        const groupId = await rootDispatch.groups.getGroupId();
        if (!groupId) return;

        const userOperators = useUserOperators(groupId);
        const mappedUser = mapAppBaseModelToDatabaseBaseModel(user);
        const expectedPayload = [...state.data.firestore.filter(({ id }) => id !== user.id), mappedUser];
        const ensureExpectedPayload = await dispatch.ensureFirestoreListener(expectedPayload);

        userOperators
          .update(user.id)(mappedUser)
          .then(() => ensureExpectedPayload())
          .catch(error => console.error('Error updating user:', user, error));
      },
      delete: async (context, user: App.User): Promise<void> => {
        const { state, getters, dispatch, rootDispatch } = actionContext(context);

        if (!user.id) return;
        if (!getters.getCurrentUser?.authorityLevel) return;
        if (!user.authorityLevel) return;
        if (getters.getCurrentUser.authorityLevel < user.authorityLevel) return;

        const groupId = await rootDispatch.groups.getGroupId();
        if (!groupId) return;

        const userOperators = useUserOperators(groupId);
        const expectedPayload = state.data.firestore.filter(({ id }) => id !== user.id);
        const ensureExpectedPayload = await dispatch.ensureFirestoreListener(expectedPayload);

        userOperators
          .delete(user.id)
          .then(() => ensureExpectedPayload())
          .catch(error => console.error('Error deleting user: ', user.id, error));
      }
    }
  }
);

const getterContext = (args: [unknown, unknown, unknown, unknown]) => moduleGetterContext(args, usersModule);
const actionContext = (context: ActionContext<unknown, unknown>) => moduleActionContext(context, usersModule);
